import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useProducts, useDeleteProduct, useCategories, useBrands, useApproveProduct, useRejectProduct, usePendingProductCount, type CatalogProduct } from '../hooks/useCatalogApi';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Package, Archive, Pencil, Trash2, RotateCcw, ChevronLeft, ChevronRight, Plus, Layers, Tag, Check, X, Clock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUser } from '@/shared/hooks/useAuth';
import { ModuleGuide } from '@/shared/components/ui/ModuleGuide';
import { formatINRPaise } from '@/shared/utils/currency';

interface Props {
  onCreateNew: () => void;
  onEdit: (id: string) => void;
  initialStatusFilter?: string;
}

interface StatChipProps {
  label: string;
  value: string | number;
  sublabel?: string;
  icon: React.ReactNode;
  color: string;
  index: number;
}

function StatChip({ label, value, sublabel, icon, color, index }: StatChipProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: index * 0.04 }}
      whileHover={{ y: -2 }}
      className="group"
    >
      <Card className="p-4 transition-shadow duration-200 ease-standard hover:shadow-surface-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-muted-foreground truncate">{label}</p>
            <p className="text-2xl font-bold text-foreground mt-1 tabular-nums">
              {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
            </p>
            {sublabel && <p className="text-body-xs text-muted-foreground mt-0.5">{sublabel}</p>}
          </div>
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 ease-standard group-hover:scale-110"
            style={{
              background: `linear-gradient(135deg, ${color}29, ${color}0D)`,
              boxShadow: `0 0 0 1px ${color}26`,
              color,
            }}
          >
            {icon}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export function ProductsPage({ onCreateNew, onEdit, initialStatusFilter }: Props) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(initialStatusFilter || 'active');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (initialStatusFilter) {
      setStatusFilter(initialStatusFilter);
      setPage(1);
    }
  }, [initialStatusFilter]);

  const user = useUser();
  const isAdmin = ['superadmin', 'admin', 'department_admin', 'ceo', 'owner'].includes(user?.role || '');
  const isApprover = isAdmin ||
    Boolean(user?.username === 'account' || /abhij/i.test(user?.username || '') || /abhij/i.test((user as any)?.display_name || (user as any)?.name || '') || (user as any)?.department === 'accounts');
  const { data: pendingCount = 0 } = usePendingProductCount();
  const approveMutation = useApproveProduct();
  const rejectMutation = useRejectProduct();

  const { data, isLoading, isFetching } = useProducts({
    page,
    search: debouncedSearch || undefined,
    categoryId: categoryFilter || undefined,
    brandId: brandFilter || undefined,
    approvalStatus: statusFilter === 'pending_approval' ? 'pending_approval' : statusFilter === 'all' ? 'all' : undefined,
  });
  const { data: categories } = useCategories();
  const { data: brands } = useBrands();
  const deleteMutation = useDeleteProduct();

  const allProducts = data?.data || [];
  const pagination = data?.pagination;

  // Filter active vs archived vs pending items
  const products = allProducts.filter(p => {
    if (statusFilter === 'pending_approval') return p.approvalStatus === 'pending_approval' || Boolean(p.pendingEdit?.requestedAt);
    if (statusFilter === 'active') return p.isActive !== false && p.approvalStatus !== 'pending_approval';
    if (statusFilter === 'archived') return p.isActive === false;
    return true;
  });

  const handleApprove = async (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await approveMutation.mutateAsync(id);
      toast.success(`"${name}" approved successfully!`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to approve item.');
    }
  };

  const handleReject = async (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const reason = window.prompt(`Enter reason for rejecting "${name}":`);
    if (reason === null) return;
    try {
      await rejectMutation.mutateAsync({ id, reason: reason.trim() });
      toast.success(`"${name}" rejected.`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to reject item.');
    }
  };

  // "Field: old → new" for an edit request, so an approver sees what changes.
  const FIELD_LABELS: Record<string, string> = {
    name: 'Name', sku: 'SKU', hsnSac: 'HSN', grade: 'Grade', packSize: 'Pack size', qtyPerPack: 'Pack qty',
    packagingType: 'Packaging', baseProductName: 'Product', selector: 'Selector', sellingPrice: 'Selling price',
    purchasePrice: 'Purchase price', mrp: 'MRP', minSellingPrice: 'Min. price', description: 'Description',
    totalPackSize: 'Case size', withMark: 'With mark', isActive: 'Active', notForSelling: 'Not for selling',
  };
  const editSummary = (p: CatalogProduct) => Object.entries(p.pendingEdit?.changes || {})
    .filter(([k]) => !k.endsWith('Id') && !['minSellingPriceMinor', 'marginPercent', 'tdsDocument', 'msdsDocument', 'images', 'packSizes'].includes(k))
    .map(([k, v]) => `${FIELD_LABELS[k] || k}: ${String((p as unknown as Record<string, unknown>)[k] ?? '—')} → ${String(v ?? '—')}`);

  const handleArchiveToggle = async (id: string, name: string, isCurrentlyActive: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteMutation.mutateAsync({ id, permanent: false });
      toast.success(isCurrentlyActive ? `"${name}" archived.` : `"${name}" restored.`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update item status.');
    }
  };

  const handlePermanentDelete = async (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to permanently delete "${name}"? This action cannot be undone.`)) return;
    try {
      await deleteMutation.mutateAsync({ id, permanent: true });
      toast.success(`"${name}" permanently deleted.`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to delete item.');
    }
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === products.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(products.map(p => p._id));
    }
  };

  const toggleSelectItem = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(i => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  return (
    <div className="space-y-4">
      {pendingCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">
                {pendingCount} Product{pendingCount > 1 ? 's' : ''} Awaiting Approval
              </h4>
              <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                {isApprover
                  ? 'Submitted by sales / BDM team members. Review details and approve to make active in the catalog.'
                  : 'Submitted products are currently under review by Admin, Dept Admin, or Super Admin. You can track your pending submissions below.'}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => { setStatusFilter('pending_approval'); setPage(1); }}
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold shrink-0"
          >
            {isApprover ? `Review Pending (${pendingCount})` : `View Pending (${pendingCount})`}
          </Button>
        </motion.div>
      )}

      {(pagination?.total ?? 0) > 0 && (
        <div className={`grid grid-cols-1 ${pendingCount > 0 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-4`}>
          <StatChip index={0} label="Total Items" value={pagination?.total ?? allProducts.length} sublabel="Products & services" icon={<Package className="h-5 w-5" />} color="#3b82f6" />
          <StatChip index={1} label="Categories" value={categories?.length ?? 0} sublabel="Item groupings" icon={<Layers className="h-5 w-5" />} color="#8b5cf6" />
          <StatChip index={2} label="Brands" value={brands?.length ?? 0} sublabel="Manufacturer labels" icon={<Tag className="h-5 w-5" />} color="#06b6d4" />
          {pendingCount > 0 && (
            <StatChip index={3} label="Pending Approval" value={pendingCount} sublabel={isApprover ? "Requires Admin/Dept Admin review" : "In review queue"} icon={<Clock className="h-5 w-5" />} color="#f59e0b" />
          )}
        </div>
      )}
      <Card className="border shadow-sm h-full flex flex-col rounded-xl overflow-hidden">
        <CardHeader
          title={
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold tracking-tight">All Items</h2>
              <span className="text-xs bg-slate-100 dark:bg-slate-800 text-muted-foreground px-2 py-0.5 rounded font-mono font-medium tabular-nums">
                {(pagination?.total || 0).toLocaleString('en-IN')}
              </span>
            </div>
          }
          subtitle="Item master with purchase rates, selling rates, stock levels, and usage units"
          action={
            <Button size="sm" onClick={onCreateNew} className="bg-primary hover:bg-primary-dim text-white font-semibold">
              <Plus className="h-4 w-4 mr-1" /> New
            </Button>
          }
        />
        
        {/* Filters */}
        <div className="px-4 py-3 border-b flex flex-wrap gap-3 items-center bg-slate-50/50 dark:bg-slate-900/50">
          <div className="relative w-64">
            <Input
              name="search"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search in Items..."
              className="w-full bg-white dark:bg-slate-950 pr-8"
            />
            {isFetching && (
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="w-full sm:w-44">
            <Select
              name="status"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              options={[
                { value: 'active', label: 'Active Items' },
                { value: 'pending_approval', label: pendingCount > 0 ? `Pending Approval (${pendingCount})` : 'Pending Approval' },
                { value: 'archived', label: 'Archived Items' },
                { value: 'all', label: 'All Items' },
              ]}
              className="bg-white dark:bg-slate-950"
            />
          </div>
          <div className="w-full sm:w-52">
            <Select
              name="category"
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
              options={[{ value: '', label: 'All Categories' }, ...(categories || []).map(c => ({ value: c._id, label: c.name }))]}
              className="bg-white dark:bg-slate-950"
            />
          </div>
          <div className="w-full sm:w-52">
            <Select
              name="brand"
              value={brandFilter}
              onChange={e => { setBrandFilter(e.target.value); setPage(1); }}
              options={[{ value: '', label: 'All Brands' }, ...(brands || []).map(b => ({ value: b._id, label: b.name }))]}
              className="bg-white dark:bg-slate-950"
            />
          </div>
        </div>

        <CardBody className="flex-1 p-0 overflow-auto">
          {isLoading && !data ? (
            <div className="p-8 space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : products.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-30 text-blue-600" />
              <p className="font-semibold text-foreground text-base">No Items Found</p>
              <p className="text-sm mt-1">Add products or services to start tracking stock, purchasing, and sales.</p>
              <Button size="sm" className="mt-4 bg-primary hover:bg-primary-dim text-white font-semibold" onClick={onCreateNew}>
                <Plus className="h-4 w-4 mr-1" /> New Item
              </Button>
            </div>
          ) : (
            <table className="w-full text-xs text-left border-collapse">
              <thead className="text-[11px] font-bold uppercase bg-slate-100 dark:bg-slate-900 text-muted-foreground border-b border-border sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={products.length > 0 && selectedItems.length === products.length}
                      onChange={toggleSelectAll}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3">NAME</th>
                  <th className="px-4 py-3">SKU</th>
                  <th className="px-4 py-3">CoA ACCOUNT (STEP 1)</th>
                  <th className="px-4 py-3 text-right">PURCHASE RATE</th>
                  <th className="px-4 py-3 text-right">RATE</th>
                  <th className="px-4 py-3 text-right">STOCK ON HAND</th>
                  <th className="px-4 py-3">USAGE UNIT</th>
                  <th className="px-4 py-3 text-right">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {products.map(p => {
                  const isSelected = selectedItems.includes(p._id);
                  const pAny = p as any;
                  const incomeAcc = pAny.incomeAccountId;
                  const isActive = p.isActive !== false;
                  return (
                    <tr
                      key={p._id}
                      className={`hover:bg-slate-50/80 dark:hover:bg-slate-900/60 cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-50/50 dark:bg-blue-950/40' : ''
                      } ${!isActive ? 'opacity-60 bg-slate-50 dark:bg-slate-900/30' : ''}`}
                      onClick={() => onEdit(p._id)}
                    >
                      <td className="px-3 py-3 text-center" onClick={e => toggleSelectItem(p._id, e)}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-muted-foreground shrink-0 text-base">
                            {p.packagingType === 'Barrel' ? '🛢️' : p.packagingType === 'Can' ? '🥫' : p.packagingType === 'Bucket' ? '🪣' : '📦'}
                          </div>
                          <div>
                            <span className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                              {p.name}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {p.grade && (
                                <span className="text-[10px] bg-blue-50 text-blue-700 font-bold px-1.5 py-0.5 rounded border border-blue-200">
                                  Grade: {p.grade}
                                </span>
                              )}
                              {p.packagingType && (
                                <span className="text-[10px] bg-slate-100 text-slate-700 font-semibold px-1.5 py-0.5 rounded border border-slate-200">
                                  {p.packagingType}
                                </span>
                              )}
                              {(pAny.withMark === false) && (
                                <span className="text-[10px] bg-orange-50 text-orange-700 font-bold px-1.5 py-0.5 rounded border border-orange-200">
                                  Without Mark
                                </span>
                              )}
                              {p.approvalStatus === 'pending_approval' && (
                                <span className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 px-1.5 py-0.5 rounded font-bold border border-amber-300 dark:border-amber-700">
                                  Pending Approval
                                </span>
                              )}
                              {p.pendingEdit?.requestedAt && p.approvalStatus !== 'pending_approval' && (
                                <span className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 px-1.5 py-0.5 rounded font-bold border border-amber-300 dark:border-amber-700"
                                  title={editSummary(p).join('\n')}>
                                  Edit request{p.pendingEdit.requestedByName ? ` · ${p.pendingEdit.requestedByName}` : ''}
                                </span>
                              )}
                              {p.approvalStatus === 'rejected' && (
                                <span className="text-[10px] bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 px-1.5 py-0.5 rounded font-bold border border-rose-300 dark:border-rose-700">
                                  Rejected
                                </span>
                              )}
                              {!isActive && p.approvalStatus !== 'pending_approval' && (
                                <span className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 px-1.5 py-0.5 rounded font-semibold">
                                  Archived
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-muted-foreground text-[11px]">{p.sku || '-'}</td>
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                          {incomeAcc?.code ? `${incomeAcc.code} - ${incomeAcc.name}` : '4010 - Sales Revenue'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatINRPaise(p.purchasePrice || 0)}
                      </td>
                      <td className="px-4 py-3 text-right font-extrabold text-slate-900 dark:text-slate-100">
                        {formatINRPaise(p.sellingPrice || 0)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {p.isStockManaged ? (
                          <span className="font-bold text-emerald-600 dark:text-emerald-400">
                            {(p.currentStock || 0).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">0.00</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground font-medium">
                        {/* Show "1.2 LTR × 16" when both packSize and qtyPerPack are set */}
                        {p.packSize && pAny.qtyPerPack > 0
                          ? <span>{p.packSize} <span className="text-slate-400 font-normal">×</span> <span className="font-bold text-slate-700 dark:text-slate-200">{pAny.qtyPerPack}</span></span>
                          : (p.packSize || pAny.unitName || p.unitId?.name || 'LTR')
                        }
                      </td>
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {p.approvalStatus === 'pending_approval' || p.pendingEdit?.requestedAt ? (
                            isApprover ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title="Approve Product"
                                  aria-label={`Approve ${p.name}`}
                                  onClick={(e) => handleApprove(p._id, p.name, e)}
                                  className="h-7 px-2.5 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700 shadow-xs"
                                >
                                  <Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title="Reject Product"
                                  aria-label={`Reject ${p.name}`}
                                  onClick={(e) => handleReject(p._id, p.name, e)}
                                  className="h-7 px-2.5 text-xs font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border-rose-300 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-700 shadow-xs"
                                >
                                  <X className="h-3.5 w-3.5 mr-1 text-rose-600" /> Reject
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Edit Item"
                                  aria-label={`Edit ${p.name}`}
                                  onClick={(e) => { e.stopPropagation(); onEdit(p._id); }}
                                  className="h-7 w-7 p-0 text-slate-500 hover:text-foreground"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded inline-flex items-center gap-1">
                                  <Clock className="h-3 w-3" /> In Review
                                </span>
                                {(pAny.createdBy === user?.id || !pAny.createdBy) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Edit My Submission"
                                    aria-label={`Edit ${p.name}`}
                                    onClick={(e) => { e.stopPropagation(); onEdit(p._id); }}
                                    className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/50"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </>
                            )
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Edit Item"
                                aria-label={`Edit ${p.name}`}
                                onClick={(e) => { e.stopPropagation(); onEdit(p._id); }}
                                className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/50"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title={isActive ? "Archive Item" : "Restore Item"}
                                    aria-label={`${isActive ? 'Archive' : 'Restore'} ${p.name}`}
                                    onClick={(e) => handleArchiveToggle(p._id, p.name, isActive, e)}
                                    className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/50"
                                  >
                                    {isActive ? <Archive className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Delete Item Permanently"
                                    aria-label={`Delete ${p.name}`}
                                    onClick={(e) => handlePermanentDelete(p._id, p.name, e)}
                                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/50"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50">
            <span className="text-xs text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Reference guide */}
      <ModuleGuide
        summary="Define each product or service's identity, cost, selling rate and stock rules here. This master data is reused across purchasing, inventory, sales invoices and POS."
        steps={[
          'Create the item: set its name, SKU, HSN/SAC, unit and tax profile.',
          'Purchase price is the supplier cost; selling price is the default rate on invoices and POS.',
          'Posted goods receipts increase stock; sales and dispatches reduce it.',
        ]}
        formulas={[
          { label: 'Margin per unit', formula: 'Selling price − Purchase price', meaning: 'Expected gross profit per unit, before tax.' },
          { label: 'Markup %', formula: '(Margin ÷ Purchase price) × 100', meaning: 'Percentage added on top of cost.' },
          { label: 'Stock on hand', formula: 'Opening + Received + Returns − Sold − Issued ± Adjustments' },
        ]}
      />
    </div>
  );
}
